'use client';

import * as React from 'react';
import { useEffect, useRef } from 'react';
import HotelDetailBiff from '@/app/common/biff/hotelDetailPage/HotelDetailBiff.tsx';
import { URLParameters } from '@/app/common/urlParameters';
import { PAGE_TYPE } from '@/app/common/pageType';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import ErrorBoundary from '@/app/common/components/ErrorBoundary';
import { clientAppLog } from '@/app/components/search/logging/clientAppLog.ts';
import { isValidGtmKey } from '@/app/common/tracking/gtmKey.ts';
import { hotjarEvents, HotjarEvent } from '@/app/common/hotjar/hotjarEvents.ts';
import useCookieConsent from '@/app/common/hooks/useCookieConsent';
import getSearchParams from '../search/common/getSearchParams';

type PageLayoutProps = React.HTMLAttributes<HTMLElement>;

/**
 * Common layout to the tour operator and hotel only pages
 *
 * The layout of the page is defined by css grid and the children of the
 * component can be assigned a grid area according to the grid template.
 * Further common external libraries can be initialized here like for example the LMHeaderLoader, Biff etc.
 */
export function PageLayout(props: PageLayoutProps) {
  const { staticData, localization, commonData } = usePageWrapperData();
  const gtmKey = commonData?.bpValues?.gtmKey;
  const searchParams = getSearchParams();
  const pageType = searchParams[URLParameters.PAGE_TYPE];
  const apexEnabled = localization?.configValuesApp?.apexEnabled;
  const evolutionEnabled = localization?.configValuesApp?.evolutionEnabled;
  const hasConsent = useCookieConsent(commonData?.isLocal);
  const apexFired = useRef(false);
  const firedPageTypes = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!hasConsent || !evolutionEnabled || firedPageTypes.current.has(pageType)) {
      return;
    }
    const event =
      pageType === PAGE_TYPE.HOTEL_DETAIL
        ? HotjarEvent.EVO_DETAILS
        : pageType === PAGE_TYPE.TRANSPORT_DETAIL
          ? HotjarEvent.EVO_FLIGHT
          : undefined;
    if (!event) {
      return;
    }
    firedPageTypes.current.add(pageType);
    hotjarEvents([event]);
  }, [hasConsent, evolutionEnabled, pageType]);

  useEffect(() => {
    if (hasConsent && apexEnabled && pageType === PAGE_TYPE.HOTEL_DETAIL && !apexFired.current) {
      apexFired.current = true;
      hotjarEvents([HotjarEvent.EVO_DETAILS_APEX]);
    }
  }, [hasConsent, apexEnabled, pageType]);

  useEffect(() => {
    if (isValidGtmKey(gtmKey)) {
      return;
    }
    clientAppLog({
      level: 'WARN',
      codeSourceName: 'getGTMtrackingId',
      logMessage: `Gtm id not set or invalid. gtmKey:${gtmKey || 'undefined'}, brand:${staticData?.brandValues?.label || 'undefined'}, bp from query:${staticData?.businessProfileIdFromQuery || 'undefined'}, bp used:${localization?.businessProfile || 'undefined'}`,
      url: window.location.href,
    });
  }, [gtmKey, staticData?.brandValues?.label, staticData?.businessProfileIdFromQuery, localization?.businessProfile]);

  return (
    <>
      {pageType === PAGE_TYPE.HOTEL_DETAIL ? <HotelDetailBiff /> : null}
      <ErrorBoundary id="PageLayout" showError={true} resetKeys={[pageType]}>
        {props.children}
      </ErrorBoundary>
    </>
  );
}
